/**
 * findTypedInsertColumns
 *
 * Detect whether the cursor sits inside an `INSERT INTO <table> (... | ...)`
 * column-list slot, and if so return the bare column-name tokens already
 * typed in the list before the cursor (lowercased, dedup-preserved).
 *
 * Used by per-engine resolvers to suppress already-typed columns from the
 * next slot's suggestion list — `INSERT INTO t (a, |)` shouldn't re-offer
 * `a`. The harness exercises this via Script 7 / Script 11 / Script 16 /
 * Script 25 (etc.) where checkpoint cells say "Must NOT include: already-
 * typed `Foo`".
 *
 * Returns `null` when the cursor isn't inside an INSERT column-list (this
 * keeps callers' branch logic simple — they only filter when the helper
 * is opted in).
 *
 * Engine-agnostic at the token level: the per-engine tokenizer recognises
 * each dialect's quoting / comment forms, but the INSERT column-list
 * grammar shape (`INSERT [<mods>] INTO <[schema.]table> (` …) is uniform.
 */

import { tokenize, type Token } from './tokenizer';
import type { DatabaseEngineId } from '@/core/engines';

/**
 * The relation an INSERT column list belongs to, as written in source (quoting
 * stripped, case preserved). `INSERT INTO db.dbo.t (▮` yields
 * `{ database: 'db', schema: 'dbo', table: 't' }`.
 */
export type InsertColumnListTarget = {
  readonly database?: string;
  readonly schema?: string;
  readonly table: string;
};

type InsertColumnListSite = {
  readonly tokens: readonly Token[];
  readonly lparenIdx: number;
  readonly cursorTokenIdx: number;
  readonly target: InsertColumnListTarget;
};

/**
 * The INSERT column-list site the cursor sits in, or null. Shared by
 * {@link findTypedInsertColumns} (which reads the already-committed names out
 * of it) and {@link findInsertColumnListTarget} (which reads the owning
 * relation) so the `INSERT [<mods>] INTO <[db.][schema.]table> (` prefix walk
 * has exactly one implementation.
 */
function locateInsertColumnList(
  source: string,
  cursorOffset: number,
  statementStart: number,
  statementEnd: number,
  engine: DatabaseEngineId,
): InsertColumnListSite | null {
  const tokens = tokenize(source, statementStart, statementEnd, engine);

  // Locate the token at-or-just-before the cursor.
  let cursorTokenIdx = -1;
  for (let i = 0; i < tokens.length; i += 1) {
    if (tokens[i].start >= cursorOffset) break;
    cursorTokenIdx = i;
  }
  if (cursorTokenIdx < 0) return null;

  // Walk back, counting paren depth, to find the unmatched `(` that opens
  // the enclosing list. Abort at a semicolon (different statement) or if
  // depth tries to go negative beyond an `lparen` (shouldn't happen with
  // valid input).
  let depth = 0;
  let lparenIdx = -1;
  for (let i = cursorTokenIdx; i >= 0; i -= 1) {
    const t = tokens[i];
    if (t.type === 'semicolon') return null;
    if (t.type === 'rparen') {
      depth += 1;
      continue;
    }
    if (t.type === 'lparen') {
      if (depth === 0) {
        lparenIdx = i;
        break;
      }
      depth -= 1;
    }
  }
  if (lparenIdx < 0) return null;

  // The `(` must be immediately preceded by the table-name component of
  // an `INSERT [<mods>] INTO <[catalog.][schema.]table>` prefix. Walk back
  // over exactly one qualified name (1-3 dotted parts), then expect
  // `INTO`, then `INSERT` / `REPLACE` (modulo dialect-specific modifiers).
  let j = lparenIdx - 1;
  if (j < 0 || (tokens[j].type !== 'ident' && tokens[j].type !== 'kw')) {
    return null;
  }
  // Name parts collected right-to-left: table, then schema, then database.
  const nameParts: string[] = [stripIdentQuotes(tokens[j].text)];
  j -= 1; // consumed the table-name token
  for (let dotted = 0; dotted < 2; dotted += 1) {
    if (j < 0 || tokens[j].type !== 'dot') break;
    j -= 1; // consumed the dot
    if (j < 0 || (tokens[j].type !== 'ident' && tokens[j].type !== 'kw')) {
      return null;
    }
    nameParts.push(stripIdentQuotes(tokens[j].text));
    j -= 1; // consumed the qualifier (schema / catalog)
  }
  if (j < 0 || identUpper(tokens[j]) !== 'INTO') return null;
  // Optional INSERT modifiers (varies by engine):
  //   - MySQL: LOW_PRIORITY / DELAYED / HIGH_PRIORITY / IGNORE
  //   - SQLite: OR (REPLACE | IGNORE | ROLLBACK | ABORT | FAIL)
  //   - Postgres: <none on this side of INTO>
  //   - Oracle / MSSQL: <none on this side of INTO>
  j -= 1;
  while (j >= 0) {
    const u = identUpper(tokens[j]);
    if (u === 'INSERT' || u === 'REPLACE') break;
    if (
      u === 'OR' ||
      u === 'IGNORE' ||
      u === 'REPLACE' ||
      u === 'ROLLBACK' ||
      u === 'ABORT' ||
      u === 'FAIL' ||
      u === 'LOW_PRIORITY' ||
      u === 'HIGH_PRIORITY' ||
      u === 'DELAYED'
    ) {
      j -= 1;
      continue;
    }
    return null;
  }
  if (j < 0) return null;

  return {
    tokens,
    lparenIdx,
    cursorTokenIdx,
    target: {
      table: nameParts[0]!,
      ...(nameParts[1] ? { schema: nameParts[1] } : {}),
      ...(nameParts[2] ? { database: nameParts[2] } : {}),
    },
  };
}

/**
 * The relation an `INSERT INTO <table> (… ▮ …)` column list belongs to, or
 * null when the cursor is not inside such a list. Resolvers use it to look
 * the target's columns up in the snapshot — e.g. to demote the ones the
 * engine generates itself (IDENTITY / computed).
 */
export function findInsertColumnListTarget(
  source: string,
  cursorOffset: number,
  statementStart: number,
  statementEnd: number,
  engine: DatabaseEngineId,
): InsertColumnListTarget | null {
  return locateInsertColumnList(source, cursorOffset, statementStart, statementEnd, engine)
    ?.target ?? null;
}

export function findTypedInsertColumns(
  source: string,
  cursorOffset: number,
  statementStart: number,
  statementEnd: number,
  engine: DatabaseEngineId,
): readonly string[] | null {
  const site = locateInsertColumnList(source, cursorOffset, statementStart, statementEnd, engine);
  if (!site) return null;
  const { tokens, lparenIdx, cursorTokenIdx } = site;

  // Collect column tokens between `(` and cursor. A token is "committed"
  // (and thus typed) only if a comma follows it before the cursor — the
  // in-progress identifier at the cursor itself is excluded so the user
  // can still see the matching candidate as they type.
  const typed: string[] = [];
  for (let i = lparenIdx + 1; i <= cursorTokenIdx; i += 1) {
    const t = tokens[i];
    if (t.type !== 'ident' && t.type !== 'kw') continue;
    // Find the next token that's neither part of a qualified name (ident/
    // kw/dot) nor whitespace. If it's a comma, this slot is committed.
    let k = i + 1;
    while (k <= cursorTokenIdx) {
      const next = tokens[k];
      if (next.type === 'ident' || next.type === 'kw' || next.type === 'dot') {
        k += 1;
        continue;
      }
      break;
    }
    if (k > cursorTokenIdx) break;
    if (tokens[k].type !== 'comma') continue;
    typed.push(stripIdentQuotes(t.text).toLowerCase());
    // Skip the rest of the qualified-name chain we just consumed so we
    // don't double-count `schema` and `table` in `schema.col,` — though
    // INSERT column lists don't actually permit schema-qualified columns,
    // this is just defensive.
    i = k;
  }
  return typed;
}

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return stripIdentQuotes(t.text).toUpperCase();
}

function stripIdentQuotes(s: string): string {
  if (s.length < 2) return s;
  const first = s[0];
  const last = s[s.length - 1];
  if (first === '`' && last === '`') return s.slice(1, -1);
  if (first === '"' && last === '"') return s.slice(1, -1);
  if (first === '[' && last === ']') return s.slice(1, -1);
  return s;
}

/**
 * `UPDATE t SET a = 1, b = 2, |` — the column names already assigned in the
 * SET list before the cursor (lowercased, in list order).
 *
 * Also covers the SET lists that are not statement-initial: `MERGE … WHEN
 * MATCHED THEN UPDATE SET a = 1, |` and MySQL's `INSERT … ON DUPLICATE KEY
 * UPDATE a = 1, |` / `INSERT … SET a = 1, |`, since all of them are "the
 * comma-separated assignment list that follows the nearest top-level `SET`".
 *
 * Returns `null` when the cursor isn't inside such a list, so callers filter
 * only when the helper is confident. Mirrors `findTypedInsertColumns`: only
 * *committed* assignments (a comma follows before the cursor) are reported, so
 * the identifier being typed still matches its own candidate.
 *
 * Callers must decide whether a repeat is actually illegal — it is in
 * Postgres, SQL Server and Oracle, but SQLite and MySQL accept a duplicate
 * target and use the last assignment.
 */
export function findAssignedSetColumns(
  source: string,
  cursorOffset: number,
  statementStart: number,
  statementEnd: number,
  engine: DatabaseEngineId,
): readonly string[] | null {
  const tokens = tokenize(source, statementStart, statementEnd, engine);
  let cursorTokenIdx = -1;
  for (let i = 0; i < tokens.length; i += 1) {
    if (tokens[i].start >= cursorOffset) break;
    cursorTokenIdx = i;
  }
  if (cursorTokenIdx < 0) return null;

  // Walk back to the nearest `SET` at the cursor's own paren depth. A closed
  // group on the way (`SET a = COALESCE(x, 0), |`) is skipped; an UNMATCHED
  // `(` means the cursor sits inside a sub-expression rather than the
  // assignment list, so there is nothing to report.
  let setIdx = -1;
  let depth = 0;
  for (let i = cursorTokenIdx; i >= 0; i -= 1) {
    const t = tokens[i];
    if (t.type === 'semicolon') return null;
    if (t.type === 'rparen') {
      depth += 1;
      continue;
    }
    if (t.type === 'lparen') {
      if (depth === 0) return null;
      depth -= 1;
      continue;
    }
    if (depth > 0) continue;
    const u = identUpper(t);
    if (u === 'SET') {
      setIdx = i;
      break;
    }
    // MySQL's upsert tail spells the list opener `ON DUPLICATE KEY UPDATE`,
    // with no `SET` of its own.
    if (u === 'UPDATE' && identUpper(tokens[i - 1]) === 'KEY') {
      setIdx = i;
      break;
    }
    if (u !== null && SET_LIST_TERMINATORS.has(u)) return null;
  }
  if (setIdx < 0) return null;

  // Split the list on its top-level commas and read each committed segment's
  // assignment target.
  const assigned: string[] = [];
  let segmentStart = setIdx + 1;
  let segmentDepth = 0;
  for (let i = setIdx + 1; i <= cursorTokenIdx; i += 1) {
    const t = tokens[i];
    if (t.type === 'lparen') segmentDepth += 1;
    else if (t.type === 'rparen') segmentDepth -= 1;
    else if (t.type === 'comma' && segmentDepth === 0) {
      assigned.push(...assignmentTargets(tokens, segmentStart, i));
      segmentStart = i + 1;
    }
  }
  // The caret must be at an assignment TARGET, not inside a value. Once the
  // open segment has its own `=`, the caret is on the right-hand side — and a
  // column already assigned earlier is perfectly legal to READ there
  // (`SET price = price * 1.1, total = price * ▮`).
  for (let i = segmentStart; i <= cursorTokenIdx; i += 1) {
    const t = tokens[i];
    if (t.type === 'lparen') segmentDepth += 1;
    else if (t.type === 'rparen') segmentDepth -= 1;
    else if (segmentDepth === 0 && t.type === 'op' && t.text === '=') return null;
  }
  return assigned;
}

/** Clause keywords that end an UPDATE/MERGE SET list — seeing one on the way
 *  back means the cursor is past the assignments. `WHEN` is deliberately
 *  absent: a `CASE WHEN` inside an assigned value would otherwise abort the
 *  walk before it reaches `SET`. */
const SET_LIST_TERMINATORS: ReadonlySet<string> = new Set([
  'WHERE', 'FROM', 'RETURNING', 'OUTPUT', 'ORDER', 'LIMIT', 'USING',
  'INSERT', 'DELETE', 'SELECT', 'VALUES',
]);

/** The columns assigned in `tokens[from..to)`. Usually one — the name before
 *  the segment's `=` — but Postgres also takes a row target,
 *  `SET (a, b) = (1, 2)`, which assigns every name in the leading group.
 *  Empty when the segment has no `=`, so a bare list is never mistaken for a
 *  set of assignments. */
function assignmentTargets(tokens: readonly Token[], from: number, to: number): string[] {
  if (tokens[from]?.type === 'lparen') return rowAssignmentTargets(tokens, from, to);
  let name: string | null = null;
  for (let i = from; i < to; i += 1) {
    const t = tokens[i];
    if (t.type === 'ident' || t.type === 'kw') {
      name = stripIdentQuotes(t.text).toLowerCase();
      continue;
    }
    if (t.type === 'dot') continue;
    // `=` is the assignment; anything else before it means this segment is not
    // a plain `column = value`.
    if (t.type === 'op' && t.text === '=') return name === null ? [] : [name];
    return [];
  }
  return [];
}

/** `(a, b) = (1, 2)` — every name inside the leading group, provided an `=`
 *  follows it. */
function rowAssignmentTargets(tokens: readonly Token[], from: number, to: number): string[] {
  const names: string[] = [];
  let depth = 0;
  let i = from;
  for (; i < to; i += 1) {
    const t = tokens[i];
    if (t.type === 'lparen') {
      depth += 1;
      continue;
    }
    if (t.type === 'rparen') {
      depth -= 1;
      if (depth === 0) {
        i += 1;
        break;
      }
      continue;
    }
    if (depth === 1 && (t.type === 'ident' || t.type === 'kw')) {
      names.push(stripIdentQuotes(t.text).toLowerCase());
    }
  }
  const next = tokens[i];
  return next && next.type === 'op' && next.text === '=' ? names : [];
}
